import { physicalStockQuantity, roundStockQuantity } from './stock-quantity';
export interface SalesDeliveryStockLine {
  articleId?: string | null;
  variantId?: string | null;
  /**
   * Client-side catalogue hint. The database remains authoritative; missing
   * legacy values deliberately fail closed as tracked stock.
   */
  stockTrackingEnabled?: boolean;
  quantity: number;
  /**
   * Facteur de conditionnement figé sur la ligne : nombre de pièces de base par
   * unité vendue (1 par défaut = unité de base). Vendre « 2 cartons de 12 »
   * décompte 24 pièces du stock. Absent/1 = comportement historique inchangé.
   */
  unitFactor?: number | null;
}

export interface SalesDeliveryStockEffect {
  articleId: string;
  variantId: string | null;
  quantity: number;
}

export interface SalesDeliveryWarehouseCandidate {
  id: string;
  storeId: string;
  isDefault: boolean;
}

/**
 * Builds the authoritative stock delta for a delivery.
 *
 * Free-text lines do not move stock. Lines are aggregated by article+variant
 * so two variants of one article never collapse into one stock movement.
 */
export function buildSalesDeliveryStockEffects(
  lines: readonly SalesDeliveryStockLine[],
): SalesDeliveryStockEffect[] {
  const quantities = new Map<string, SalesDeliveryStockEffect>();
  for (const line of lines) {
    if (!line.articleId) continue;
    // La quantité saisie est exprimée dans l'unité vendue (pièce, carton, ...).
    // Le stock est toujours tenu en pièces de base : on convertit ici, à
    // l'unique source de vérité des mouvements, pour que carton et pièce ne
    // divergent jamais.
    const factor = Number(line.unitFactor);
    const baseUnitsPerUnit = Number.isFinite(factor) && factor > 0 ? factor : 1;
    const quantity = physicalStockQuantity(Number(line.quantity), baseUnitsPerUnit);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      throw new Error('sales_delivery_invalid_stock_quantity');
    }
    const variantId = line.variantId ?? null;
    const key = `${line.articleId}\u0000${variantId ?? ''}`;
    const current = quantities.get(key);
    quantities.set(key, {
      articleId: line.articleId,
      variantId,
      quantity: roundStockQuantity((current?.quantity ?? 0) + quantity),
    });
  }
  return [...quantities.values()].sort(
    (left, right) =>
      left.articleId.localeCompare(right.articleId) ||
      (left.variantId ?? '').localeCompare(right.variantId ?? ''),
  );
}

/**
 * Whether the client must resolve a warehouse before submitting a delivery.
 *
 * Free-text and explicitly untracked catalogue lines need no warehouse. An
 * article line with an absent legacy hint is treated as tracked so the client
 * never silently omits a required stock target; the server re-checks the live
 * article state inside the atomic command.
 */
export function salesDeliveryRequiresWarehouse(
  lines: readonly Pick<
    SalesDeliveryStockLine,
    'articleId' | 'stockTrackingEnabled'
  >[],
): boolean {
  return lines.some(
    (line) => Boolean(line.articleId) && line.stockTrackingEnabled !== false,
  );
}

/**
 * Resolve the one warehouse that an atomic delivery may debit.
 *
 * `preferredWarehouseId` is the warehouse attached to the cash register (or
 * chosen explicitly on the delivery form). It wins whenever it belongs to the
 * source store; anything else falls back to the historical rule below, so a
 * stale or foreign preference can never target another store's stock.
 *
 * Historical rule: a single warehouse is unambiguous even without a default
 * flag. With several candidates, exactly one must be the default. Article
 * lines fail closed when neither condition holds; free-text-only deliveries
 * need no warehouse.
 */
export function resolveSalesDeliveryWarehouseId(
  lines: readonly Pick<
    SalesDeliveryStockLine,
    'articleId' | 'stockTrackingEnabled'
  >[],
  warehouses: readonly SalesDeliveryWarehouseCandidate[],
  storeId?: string | null,
  preferredWarehouseId?: string | null,
): string | null {
  if (!salesDeliveryRequiresWarehouse(lines)) return null;

  const candidates = storeId
    ? warehouses.filter((warehouse) => warehouse.storeId === storeId)
    : [...warehouses];
  if (
    preferredWarehouseId &&
    candidates.some((warehouse) => warehouse.id === preferredWarehouseId)
  ) {
    return preferredWarehouseId;
  }
  if (candidates.length === 1) return candidates[0]!.id;

  const defaults = candidates.filter((warehouse) => warehouse.isDefault);
  return defaults.length === 1 ? defaults[0]!.id : null;
}
